import { makeStyles } from '@material-ui/core/styles';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { filterToggleButtonStyle, filterToggleGroupStyle } from 'components/filters/filterToggleStyles';
import React from 'react';
import { useTranslate } from 'react-admin';

/**
 * `normal` keeps every control — scanner plus the manual ones.
 * `quick` is scanner-only: manual controls are hidden and a scan is confirmed almost
 * immediately, for walking a whole area with a scanner in hand.
 * `initial` is the first inventorization of a terminal, where the system holds no
 * expectation of what stands where: a scan records what is actually found.
 */
export enum InventorizationMode {
  normal = 'normal',
  quick = 'quick',
  initial = 'initial',
}

export const INVENTORIZATION_MODES: InventorizationMode[] = [
  InventorizationMode.normal,
  InventorizationMode.quick,
  InventorizationMode.initial,
];

// The rules come from the shared module, the components do not: the ones exported by
// components/filters/FilterToggleInput are JS wrapped in withStyles, and their inferred
// type resolves React through the second copy of @types/react in the tree (pulled in by
// @types/react-transition-group), which makes them unusable as JSX components from
// TypeScript (TS2786).
const useStyles = makeStyles((theme) => ({
  group: filterToggleGroupStyle,
  button: filterToggleButtonStyle(theme),
}));

interface InventorizationModeToggleProps {
  mode: InventorizationMode;
  onChange: (mode: InventorizationMode) => void;
  disabled?: boolean;
}

const disabledModes = [InventorizationMode.normal, InventorizationMode.quick];

export const InventorizationModeToggle: React.FC<InventorizationModeToggleProps> = ({
  mode,
  onChange,
  disabled,
}) => {
  const classes = useStyles();
  const translate = useTranslate();

  const handleChange = (_event: React.MouseEvent<HTMLElement>, next: InventorizationMode) => {
    // Deselecting is not allowed — one mode is always active.
    if (next) onChange(next);
  };

  return (
    <ToggleButtonGroup
      className={classes.group}
      value={mode}
      size="small"
      exclusive
      onChange={handleChange}
    >
      {INVENTORIZATION_MODES.map((option) => (
        <ToggleButton key={option} className={classes.button} value={option} disabled={disabled || disabledModes.includes(option)}>
          {translate(`osfc.storage_ui.inventorization.mode.${option}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
